import { Icon } from '@hologram-dimension/icon';
import _classnames from 'clsx';
import PropTypes from 'prop-types';
import React from 'react';
import { formatNumber } from '../../common/js/utilities.ts';
import Pill from '../pill/pill.jsx';

/**
  A button that displays a key metric number with some additional descriptive information.
*/
class MetricButton extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      hovered: false,
      pillHovered: false,
    };
  }

  setHovered = (isHovered) => {
    this.setState({ hovered: isHovered });
  };

  setPillHovered = (pillHovered) => {
    this.setState({ pillHovered });
  };

  render() {
    const { disabled, inactive, isLoading, number, onClick, icon, orbColor, pillProps, text } =
      this.props;
    const { hovered, pillHovered } = this.state;

    const isDisabled = disabled || number === 0 || !onClick || inactive;

    const loadingState = (
      <>
        <div
          className={_classnames('MetricButton__loading-bar', 'MetricButton__loading-bar--top')}
        />
        <div
          className={_classnames('MetricButton__loading-bar', 'MetricButton__loading-bar--middle')}
        />
        {pillProps && (
          <div
            className={_classnames(
              'MetricButton__loading-bar',
              'MetricButton__loading-bar--bottom'
            )}
          />
        )}
      </>
    );

    return (
      <button
        className={_classnames('MetricButton', {
          'MetricButton--disabled': isDisabled || isLoading,
          'MetricButton--inactive': inactive,
          'MetricButton--hovered': hovered,
          'MetricButton--pillNotHovered': !pillHovered,
          'MetricButton--isLoading': isLoading,
        })}
        onClick={!isDisabled && !isLoading && !pillHovered ? onClick : () => {}}
        type="button"
      >
        {isLoading ? (
          loadingState
        ) : (
          <div
            className="MetricButton__hoverableArea"
            onMouseEnter={() => this.setHovered(true)}
            onMouseLeave={() => this.setHovered(false)}
          >
            {!isDisabled && hovered && (
              <Icon
                className="MetricButton__icon"
                name="ArrowEastToTable"
                fill="DdsColorTextPrimaryOninverse"
              />
            )}
            <div className="MetricButton__info-wrapper">
              {orbColor && (
                <div className="MetricButton__orb" style={{ background: orbColor }}>
                  {icon}
                </div>
              )}
              <div
                className={_classnames('MetricButton__text', {
                  'MetricButton__text--withOrb': orbColor,
                })}
              >
                {text}
              </div>
            </div>
            <div
              className={_classnames('MetricButton__number', {
                'MetricButton__number--disabled': isDisabled,
                'MetricButton__number--inactive': inactive,
              })}
            >
              {number ? formatNumber(number) : 0}
            </div>
          </div>
        )}
        {pillProps && !isLoading && (
          <div
            className="MetricButton__pill"
            onMouseEnter={() => this.setPillHovered(true)}
            onMouseLeave={() => this.setPillHovered(false)}
          >
            <Pill {...pillProps} />
          </div>
        )}
      </button>
    );
  }
}

MetricButton.propTypes = {
  /**
    Disabled styles are applied and button is not clickable. Note: there is no `disabled` state applied to the button element, in order for the tooltip to remain hoverable.
  */
  disabled: PropTypes.bool,
  /**
    Renders as static unclickable button without disabled or hover state
  */
  inactive: PropTypes.bool,
  /**
    Renders in loading state
  */
  isLoading: PropTypes.bool,
  /**
    Required; main metric number rendered. When number is `0`, button is disabled.
  */
  number: PropTypes.number.isRequired,
  /**
    Text description of metric
  */
  text: PropTypes.string,
  /**
    Color of orb/dot; when not provided, no orb is rendered
  */
  orbColor: PropTypes.string,
  icon: PropTypes.node,
  /**
    When not provided, button is disabled
  */
  onClick: PropTypes.func,
  /**
    Object with prop data for a pill component to be included below the metric number
    (props available include `changeAmount`, `period`, `text`, and `tooltip`)
  */
  pillProps: PropTypes.shape({
    changeAmount: PropTypes.number,
    period: PropTypes.string,
    text: PropTypes.string,
    tooltip: PropTypes.string,
  }),
};

MetricButton.defaultProps = {
  disabled: false,
  isLoading: false,
  inactive: undefined,
  icon: undefined,
  text: undefined,
  orbColor: undefined,
  onClick: undefined,
  pillProps: undefined,
};

export default MetricButton;
